import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import { PUBLIC_BOOKING } from "@/constants/testIds";

export default function StepPatientInfo({ clinic, service, professional, dateTime, onBack, onConfirmed, linkCode, prefill }) {
  const { t } = usePublicLang();
  const required = clinic.required_patient_fields || [];
  const [form, setForm] = useState({ name: prefill?.name || "", phone: prefill?.phone || "", email: "", date_of_birth: "", emergency_contact_name: "", emergency_contact_phone: "", allergies: "", reason: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = t("step4.errors.name");
    if (form.phone.trim().length < 6) errs.phone = t("step4.errors.phone");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t("step4.errors.email");
    if (required.includes("email") && !form.email.trim()) errs.email = t("step4.errors.email");
    if (required.includes("date_of_birth") && !form.date_of_birth) errs.date_of_birth = t("step4.errors.dob");
    if (required.includes("emergency_contact")) {
      if (!form.emergency_contact_name.trim()) errs.emergency_contact_name = t("step4.errors.emergencyName");
      if (!form.emergency_contact_phone.trim()) errs.emergency_contact_phone = t("step4.errors.emergencyPhone");
    }
    if (required.includes("allergies") && !form.allergies.trim()) errs.allergies = t("step4.errors.allergies");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    setApiError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/clinics/${clinic.slug}/book`, {
        service_id: service.id,
        professional_id: professional?.id || "any",
        date: dateTime.date,
        time: dateTime.time,
        patient_name: form.name.trim(),
        patient_phone: form.phone.trim(),
        patient_email: form.email.trim() || undefined,
        date_of_birth: form.date_of_birth || undefined,
        emergency_contact_name: form.emergency_contact_name.trim() || undefined,
        emergency_contact_phone: form.emergency_contact_phone.trim() || undefined,
        allergies: form.allergies.trim() || undefined,
        reason: form.reason.trim() || undefined,
        link_code: linkCode || undefined,
      });
      onConfirmed(data);
    } catch (err) {
      setApiError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-[#5C554D] underline underline-offset-4 mb-6 inline-block"
        data-testid={`${PUBLIC_BOOKING.backButtonPrefix}4`}
      >
        &larr; {t("common.back")}
      </button>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1 block">{t("step4.name")}</label>
          <Input value={form.name} onChange={set("name")} className="h-12 rounded-xl" data-testid={PUBLIC_BOOKING.nameInput} />
          {errors.name && <p className="text-xs text-[#9E4747] mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">{t("step4.phone")}</label>
          <Input value={form.phone} onChange={set("phone")} type="tel" className="h-12 rounded-xl" data-testid={PUBLIC_BOOKING.phoneInput} />
          {errors.phone && <p className="text-xs text-[#9E4747] mt-1">{errors.phone}</p>}
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">
            {t("step4.email")} {!required.includes("email") && <span className="text-[#5C554D] font-normal">({t("common.optional")})</span>}
          </label>
          <Input value={form.email} onChange={set("email")} type="email" className="h-12 rounded-xl" data-testid={PUBLIC_BOOKING.emailInput} />
          {errors.email && <p className="text-xs text-[#9E4747] mt-1">{errors.email}</p>}
        </div>

        {required.includes("date_of_birth") && (
          <div>
            <label className="text-sm font-medium mb-1 block">{t("step4.dob")}</label>
            <Input value={form.date_of_birth} onChange={set("date_of_birth")} type="date" className="h-12 rounded-xl" data-testid={PUBLIC_BOOKING.dobInput} />
            {errors.date_of_birth && <p className="text-xs text-[#9E4747] mt-1">{errors.date_of_birth}</p>}
          </div>
        )}

        {required.includes("emergency_contact") && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">{t("step4.emergencyName")}</label>
              <Input value={form.emergency_contact_name} onChange={set("emergency_contact_name")} className="h-12 rounded-xl" data-testid={PUBLIC_BOOKING.emergencyNameInput} />
              {errors.emergency_contact_name && <p className="text-xs text-[#9E4747] mt-1">{errors.emergency_contact_name}</p>}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t("step4.emergencyPhone")}</label>
              <Input value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} type="tel" className="h-12 rounded-xl" data-testid={PUBLIC_BOOKING.emergencyPhoneInput} />
              {errors.emergency_contact_phone && <p className="text-xs text-[#9E4747] mt-1">{errors.emergency_contact_phone}</p>}
            </div>
          </div>
        )}

        {required.includes("allergies") && (
          <div>
            <label className="text-sm font-medium mb-1 block">{t("step4.allergies")}</label>
            <Textarea value={form.allergies} onChange={set("allergies")} placeholder={t("step4.allergiesPlaceholder")} className="rounded-xl min-h-[70px]" data-testid={PUBLIC_BOOKING.allergiesInput} />
            {errors.allergies && <p className="text-xs text-[#9E4747] mt-1">{errors.allergies}</p>}
          </div>
        )}

        <div>
          <label className="text-sm font-medium mb-1 block">
            {t("step4.reason")} <span className="text-[#5C554D] font-normal">({t("common.optional")})</span>
          </label>
          <Textarea value={form.reason} onChange={set("reason")} className="rounded-xl min-h-[90px]" data-testid={PUBLIC_BOOKING.reasonInput} />
        </div>

        {apiError && <p className="text-sm text-[#9E4747] text-center" data-testid={PUBLIC_BOOKING.bookingError}>{apiError}</p>}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-14 bg-[var(--brand-accent)] text-white rounded-md text-sm font-medium uppercase tracking-[0.16em] transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 mt-4"
          data-testid={PUBLIC_BOOKING.confirmButton}
        >
          {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
          {t("step4.confirm")}
        </button>
      </div>
    </div>
  );
}
